/**
 * Pure builder + helpers for the Team of Agents derived view.
 *
 * S1 (this slice): missions-only path. S3 will extend with standalone
 * harness tasks merged in via `MissionNodeState.taskId` dedup.
 *
 * No React, no zustand, no IO. Pure functions over `MissionSlot` +
 * (in S3) `HarnTaskSummary`. Easy to unit-test; mirror of the
 * shipped server-side `team-status-digest.ts` builder.
 */
import { missionNodes } from "@/api/lx-mission";
import type {
  DagNode,
  MissionNodeState,
  MissionRecord,
  MissionState,
  NodeStatus,
} from "@/api/lx-mission";
import type { HarnTaskStatus } from "@/api/lx-harn";
import type { LiveNodeProgress, MissionSlot } from "@/stores/useMissionsStore";
import type { HarnTaskSlot } from "@/stores/useHarnTasksStore";

export interface MissionLeafNode {
  kind: "mission-leaf";
  nodeId: string;
  harnessId: string;
  taskId?: string;
  status: NodeStatus;
  /** Convenience: pulled from MissionNodeState for the row UI. */
  error?: { code: string; message: string };
  /** The lawyer-facing step caption — `DagNode.summary` / `node.summary`,
   *  resolved by `buildLeavesFromRecord`. Falls back to `harnessId` only
   *  when the mission predates per-node summaries. This is what the
   *  checklist child renders; NEVER the raw slug when a summary exists. */
  label: string;
  /** Live in-flight frame from the leaf — the "classificando · 60%" line.
   *  Present only while the step is running. */
  progress?: { step?: string; pct?: number; message?: string };
  /** Per-step timings for the trailing meta. */
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
}

export interface MissionTreeNode {
  kind: "mission";
  missionId: string;
  goal: string;
  state: MissionState;
  caseId?: string;
  openedAt: number;
  completedAt?: number;
  /** Set by S1's auto-expand rule: state === "awaiting_user". */
  awaitingUser: boolean;
  /** Failed-with-timeout cause (`error.code === "awaiting_user_timeout"`). */
  awaitingUserTimeout: boolean;
  /** Queue position when state === "queued" (server-computed). */
  queuePosition?: number;
  /** Optimistic cancel-in-flight flag from the SPA store. */
  isCanceling: boolean;
  /** Leaf rows derived from `record.nodes` when the full record has
   *  been drilled in. Empty when the slot is summary-only. */
  leaves: MissionLeafNode[];
  /** Who started this run — see `RunOrigin`. Requires the drilled-in
   *  record (the summary carries no sessionKey), so it reads `unknown`
   *  until the drill-in lands. */
  origin: RunOrigin;
}

export interface StandaloneHarnessTreeNode {
  kind: "standalone-harness";
  taskId: string;
  harnessId: string;
  status: HarnTaskStatus;
  caseId?: string;
  createdAt: number;
  /** Latest progress frame (step + optional pct) from the task summary. */
  progress?: { step: string; pct?: number };
  /** Optimistic cancel-in-flight flag from the SPA store. */
  isCanceling: boolean;
  // ── Ledger v2 enrichment (relay 2026-06-23) — used by HarnessRow + dialog ──
  /** Harness's friendly label (e.g. "Jurisprudence Researcher"); falls
   *  back to harnessId in render code. */
  title?: string;
  /** Per-run essence (e.g. "8 precedents found on jusbrasil"). Drives
   *  the row's caption — `summary > harnessId-fallback-shows-the-slug`. */
  summary?: string;
  /** `"infrastructure"` ⇒ platform plumbing the lawyer takes no action on
   *  (checkpoints, reconcilers). Absent/`"user_work"` ⇒ real agent work.
   *  See `isInfrastructureNode`. */
  classification?: "user_work" | "infrastructure";
  /** Set when the task is a mission leaf. Today: not surfaced on rows
   *  (mission-coordinated tasks are dedup'd out via
   *  collectMissionLeafTaskIds). Carried here so a future row could
   *  show "Part of mission ..." inline. */
  missionId?: string;
  /** Error code surfaced for the "Canceled vs Failed" display logic —
   *  `error.code === "user_cancelled"` renders as Canceled. */
  errorCode?: string;
  // ── Pane-redesign enrichment (2026-07-02) — row meta line ──
  /** Terminal stamp from the drilled record (absent on summary-only slots). */
  completedAt?: number;
  /** Run duration from the drilled record; rows fall back to
   *  `completedAt - createdAt`, then to live `now - createdAt`. */
  durationMs?: number;
  /** Rolled-up run cost (ledger enrichment on the summary). */
  costUsd?: number;
  /** Resolved model id (e.g. "gpt-5.5") — summary-level. */
  model?: string;
  /** Who started this run — see `RunOrigin`. Read from `notify.sessionKey`,
   *  which harn-service stamps for every turn-originated task. */
  origin: RunOrigin;
}

/** A SCHEDULER-started run that never produced a harness task — the
 *  `reminder` / `system` kinds, which are pure cron agent turns
 *  (`recjobs-lexide-pane-handoff` §3.5(b)). Sourced from the execution
 *  log (`schedule.runs`), so these are always TERMINAL by construction:
 *  a row exists only once the run finished. There is no live progress to
 *  show and no cancel affordance — deliberately, not an omission. */
export interface ScheduledRunTreeNode {
  kind: "scheduled-run";
  /** `${jobId}:${ts}` — a job runs repeatedly, so jobId alone is not unique. */
  runId: string;
  jobId: string;
  title: string;
  /** Job kind: "reminder" | "system" | … (open string — new kinds may ship). */
  jobKind: string;
  status: "ok" | "error" | "skipped";
  /** Run timestamp, ms since epoch (parsed from the log's ISO string). */
  at: number;
  summary?: string;
  durationMs?: number;
  error?: string;
  /** Resolved from the owning job (the run log omits it). */
  caseId?: string;
}

/** Top-level node — S3 adds the standalone-harness peer alongside
 *  the existing mission node; the sidebar redesign adds the
 *  scheduler-run peer. Discriminated by `kind`. */
export type MonitorTreeNode =
  | MissionTreeNode
  | StandaloneHarnessTreeNode
  | ScheduledRunTreeNode;

/** How a run came to exist — drives the row's attribution badge.
 *
 *  `scheduled` is the load-bearing one: a run the SCHEDULER started
 *  appears in the pane WITHOUT the lawyer having asked for anything, so
 *  it must say so. The signal is source-verified rather than guessed —
 *  OpenClaw gives an isolated cron run the session key
 *  `agent:<agentId>:cron:<jobId>:run:<runId>`
 *  (`openclaw/src/cron/isolated-agent/run.session-key-isolation.test.ts`),
 *  so a `cron:` segment identifies it unambiguously.
 *
 *  `unknown` is deliberate and must stay unbadged: absence of a session
 *  key proves nothing (mission leaves and pre-`notify` records lack one
 *  too), and inventing "Scheduled" there would misattribute a run the
 *  lawyer DID request. Recurring-jobs kinds map here as
 *  `lexiun-recurring-jobs.md` §3.1 specifies: `task`/`watch` arrive on
 *  the harness plane, `mission` on the agent plane — both carry the cron
 *  session key, so both badge correctly with no extra plumbing. */
export type RunOrigin = "scheduled" | "chat" | "unknown";

/** Extract the origin from a session key. Exported for unit testing. */
export function deriveRunOrigin(sessionKey: string | undefined): RunOrigin {
  if (typeof sessionKey !== "string" || sessionKey.trim() === "") {
    return "unknown";
  }
  // Match the `cron:` SEGMENT — substring-matching would misfire on a
  // case or job whose name merely contains the word.
  const segments = sessionKey.split(":");
  if (segments.includes("cron")) return "scheduled";
  return "chat";
}

/** True while a mission is still doing (or waiting to do) work. Drives
 *  both the sort bucket and the sidebar's "expand its steps by default"
 *  rule — an in-flight run is exactly the one whose steps the lawyer
 *  needs visible without a click. */
export function isActiveMissionState(state: MissionState): boolean {
  return (
    state === "queued" ||
    state === "submitted" ||
    state === "planning" ||
    state === "running" ||
    state === "awaiting_user" ||
    state === "synthesizing"
  );
}

/** Priority bucket for sorting top-level rows. Lower = first. */
function priorityBucket(node: MonitorTreeNode): number {
  if (node.kind === "mission") {
    if (node.awaitingUser) return 0;
    if (node.awaitingUserTimeout) return 1;
    if (
      node.state === "queued" ||
      node.state === "submitted" ||
      node.state === "planning" ||
      node.state === "running" ||
      node.state === "synthesizing"
    ) {
      return 2;
    }
    return 3;
  }
  if (node.kind === "scheduled-run") {
    // Always terminal — never competes with live work for the top slot.
    return 3;
  }
  // standalone-harness
  if (node.status === "awaiting_user") return 0;
  if (
    node.status === "queued" ||
    node.status === "dispatched" ||
    node.status === "running"
  ) {
    return 2;
  }
  // completed/failed terminal
  return 3;
}

/** When this row last CHANGED — the sort key for "latest on top"
 *  (operator directive 2026-07-19).
 *
 *  Deliberately the last-ACTIVITY stamp, not the start stamp: a run that
 *  started hours ago but just finished a step is the one the lawyer wants
 *  at the top, whereas ordering by start time would freeze a long run
 *  below newer-but-idle ones. Falls back through terminal → start so a
 *  row always has a key. */
export function nodeLastActivityAt(node: MonitorTreeNode): number {
  if (node.kind === "scheduled-run") return node.at;
  if (node.kind === "mission") {
    let latest = node.completedAt ?? node.openedAt;
    // A step that just moved makes the whole run "recently active".
    for (const leaf of node.leaves) {
      const leafAt = leaf.completedAt ?? leaf.startedAt;
      if (leafAt !== undefined && leafAt > latest) latest = leafAt;
    }
    return latest;
  }
  return node.completedAt ?? node.createdAt;
}

function compareNodes(a: MonitorTreeNode, b: MonitorTreeNode): number {
  const ba = priorityBucket(a);
  const bb = priorityBucket(b);
  if (ba !== bb) return ba - bb;
  // Latest-updated first within a bucket.
  return nodeLastActivityAt(b) - nodeLastActivityAt(a);
}

/** Map a `mission.progress` overlay state string onto the record's
 *  `NodeStatus` vocabulary. The push uses the same words the coordinator
 *  does; anything unrecognised is ignored so the record's status wins. */
function nodeStatusFromLiveState(state: string | undefined): NodeStatus | undefined {
  switch (state) {
    case "pending":
    case "dispatched":
    case "completed":
    case "failed":
    case "awaiting_user":
      return state;
    case "running":
      // The push may report a dispatched leaf as "running".
      return "dispatched";
    default:
      return undefined;
  }
}

function buildLeafNode(
  node: MissionNodeState | undefined,
  planNode: DagNode | undefined,
  live: LiveNodeProgress | undefined,
): MissionLeafNode {
  const nodeId = node?.nodeId ?? planNode?.id ?? "";
  const harnessId = node?.harnessId ?? planNode?.harnessId ?? "";
  // Label precedence: the dispatched node's summary (freshest, may be
  // refined at dispatch) → the plan's summary → the push's resolved label
  // → the raw slug as the last resort.
  // `||` not `??`: harnessId defaults to the EMPTY STRING, which `??`
  // would happily accept as a label.
  const label =
    node?.summary || planNode?.summary || live?.label || harnessId || nodeId;
  // A plan node with no entry in `record.nodes` has not been launched yet
  // — that is exactly the "☐ pending" child the flat pane could never show.
  //
  // THE RECORD WINS ONCE TERMINAL. The overlay is never cleared (a push
  // only ever replaces its own entry), so a stale frame from before a
  // step finished would otherwise resurrect it: the drill-in says
  // `completed`, the leftover push says `running`, and the checklist
  // would show a spinner and "classificando · 60%" beside work that is
  // already done. The overlay may only advance a step the record has NOT
  // yet called terminal.
  const recordStatus = node?.status;
  const recordIsTerminal =
    recordStatus === "completed" || recordStatus === "failed";
  const status: NodeStatus = recordIsTerminal
    ? recordStatus
    : (nodeStatusFromLiveState(live?.state) ?? recordStatus ?? "pending");
  // Prefer the live frame while running: it is newer than the record by
  // construction (the push precedes the drill-in).
  const progress =
    live?.message !== undefined || live?.pct !== undefined
      ? { step: node?.progress?.step, pct: live.pct, message: live.message }
      : node?.progress;
  return {
    kind: "mission-leaf",
    nodeId,
    harnessId,
    taskId: node?.taskId,
    status,
    error: node?.error,
    label,
    progress: status === "completed" || status === "failed" ? undefined : progress,
    startedAt: node?.startedAt,
    completedAt: node?.completedAt,
    durationMs: node?.durationMs,
  };
}

/** Build the checklist children for a mission.
 *
 *  ORDER IS THE PLAN'S, NOT THE MAP'S. `record.nodes` only contains
 *  LAUNCHED nodes and is keyed/iterated arbitrarily, so the previous
 *  status-priority sort both hid the not-yet-started steps and reshuffled
 *  the list under the lawyer as work progressed. Iterating `plan.nodes`
 *  gives a stable, complete "what this run will do" checklist from the
 *  moment planning finishes. Nodes present in the record but absent from
 *  the plan (defensive: a plan mutation mid-run) are appended in map
 *  order so nothing is silently dropped. */
function buildLeavesFromRecord(
  record: MissionRecord,
  liveNodes?: Map<string, LiveNodeProgress>,
): MissionLeafNode[] {
  const planNodes = record.plan?.nodes ?? [];
  // `nodes` is OPTIONAL on the wire — a queued mission has none yet. Reading it
  // directly is what blanked the whole case view on 2026-08-01; note the line
  // above already treated `plan` as absent-able, so only half the record was
  // being read defensively.
  const launched = missionNodes(record);
  const out: MissionLeafNode[] = [];
  const seen = new Set<string>();
  for (const planNode of planNodes) {
    if (!planNode?.id || seen.has(planNode.id)) continue;
    seen.add(planNode.id);
    out.push(
      buildLeafNode(launched[planNode.id], planNode, liveNodes?.get(planNode.id)),
    );
  }
  for (const node of Object.values(launched)) {
    if (seen.has(node.nodeId)) continue;
    seen.add(node.nodeId);
    out.push(buildLeafNode(node, undefined, liveNodes?.get(node.nodeId)));
  }
  return out;
}

function buildMissionNode(slot: MissionSlot): MissionTreeNode {
  const summary = slot.summary;
  const record = slot.record;
  const awaitingUser = summary.state === "awaiting_user";
  const awaitingUserTimeout =
    summary.state === "failed" &&
    record?.error?.code === "awaiting_user_timeout";
  const queuePosition =
    summary.state === "queued" && record?.queueInfo
      ? record.queueInfo.position
      : undefined;
  const leaves = record ? buildLeavesFromRecord(record, slot.liveNodes) : [];
  return {
    kind: "mission",
    missionId: summary.missionId,
    goal: summary.goal,
    state: summary.state,
    caseId: summary.scope.caseId,
    openedAt: summary.openedAt,
    completedAt: summary.completedAt,
    awaitingUser,
    awaitingUserTimeout,
    queuePosition,
    isCanceling: Boolean(slot.isCanceling),
    leaves,
    origin: deriveRunOrigin(record?.sessionKey),
  };
}

function buildStandaloneNode(slot: HarnTaskSlot): StandaloneHarnessTreeNode {
  const summary = slot.summary;
  const record = slot.record;
  return {
    kind: "standalone-harness",
    taskId: summary.taskId,
    harnessId: summary.harnessId,
    status: summary.status,
    caseId: summary.caseId,
    createdAt: summary.createdAt,
    progress: summary.progress
      ? { step: summary.progress.step, pct: summary.progress.pct }
      : undefined,
    isCanceling: Boolean(slot.isCanceling),
    // ⚠ THESE THREE ARE RECORD-ONLY IN PRACTICE — the `??` reads like a
    // fallback and is really the sole path (`git-implementation`, 2026-08-11,
    // verified against the wire). `title`, `summary` and `missionId` are NOT in
    // the `GET /tasks` LIST projection, so the right-hand arm is always
    // undefined and they work only because this tree fetches the per-task
    // RECORD. That dependency is real, not incidental: **a view that renders
    // any of them from the list alone renders blanks**, silently. Either fetch
    // the record or ask `git-implementation` to widen the projection first.
    title: record?.title ?? summary.title,
    summary: record?.summary ?? summary.summary,
    // ⚠ Was the ONE enrichment field with no record fallback while every
    // neighbour above and below has one (`title`, `summary`, `missionId`).
    // A lone inconsistency in a row of identical lines is invisible on review,
    // which is exactly how it survived.
    classification: record?.classification ?? summary.classification,
    missionId: record?.missionId ?? summary.missionId,
    errorCode: record?.error?.code,
    completedAt: record?.completedAt,
    durationMs: record?.durationMs,
    costUsd: summary.costUsd,
    model: summary.model,
    origin: deriveRunOrigin(
      (record?.notify ?? summary.notify)?.sessionKey,
    ),
  };
}

/** Build a scheduler-run node from one execution-log row. Returns null
 *  for a row with an unparseable timestamp rather than rendering a run
 *  that claims to have happened at the epoch. */
export function buildScheduledRunNode(row: {
  ts: string;
  jobId: string;
  kind: string;
  title: string;
  status: "ok" | "error" | "skipped";
  summary?: string;
  durationMs?: number;
  error?: string;
  caseId?: string;
}): ScheduledRunTreeNode | null {
  const at = Date.parse(row.ts);
  if (!Number.isFinite(at)) return null;
  return {
    kind: "scheduled-run",
    runId: `${row.jobId}:${row.ts}`,
    jobId: row.jobId,
    title: row.title,
    jobKind: row.kind,
    status: row.status,
    at,
    summary: row.summary,
    durationMs: row.durationMs,
    error: row.error,
    caseId: row.caseId,
  };
}

/** Collect every `taskId` that appears as a leaf in any mission. These
 *  tasks are mission-coordinated and must NOT also appear as standalone
 *  top-level rows. Set-based dedup; O(missions × leaves). */
function collectMissionLeafTaskIds(missions: MissionSlot[]): Set<string> {
  const out = new Set<string>();
  for (const slot of missions) {
    const record = slot.record;
    if (!record) continue;
    // Absent `nodes` ⇒ no leaves to dedupe against. Worst case a task also
    // shows as a standalone row — visible and self-correcting on the next
    // poll, unlike the throw this replaced.
    for (const node of Object.values(missionNodes(record))) {
      if (node.taskId) out.add(node.taskId);
    }
  }
  return out;
}

/** S3: builds the unified tree from missions + standalone harness
 *  tasks. The merge key is `MissionNodeState.taskId`: tasks already
 *  present as a leaf inside any mission are SKIPPED so they don't
 *  appear twice (plan §E.1 + §E.2). The remaining tasks become
 *  top-level `standalone-harness` peers of the mission rows.
 *
 *  S1 callers can pass an empty `tasks` array (back-compat); the
 *  result is mission-only with no behaviour change. */
export function buildMonitorTree(
  missions: MissionSlot[],
  tasks: HarnTaskSlot[] = [],
  scheduledRuns: Parameters<typeof buildScheduledRunNode>[0][] = [],
): MonitorTreeNode[] {
  const missionNodes = missions.map(buildMissionNode);
  const leafTaskIds = collectMissionLeafTaskIds(missions);
  const standaloneNodes = tasks
    .filter((t) => !leafTaskIds.has(t.summary.taskId))
    .map(buildStandaloneNode);
  const scheduledNodes = scheduledRuns
    .map(buildScheduledRunNode)
    .filter((n): n is ScheduledRunTreeNode => n !== null);
  const all: MonitorTreeNode[] = [
    ...missionNodes,
    ...standaloneNodes,
    ...scheduledNodes,
  ];
  return all.sort(compareNodes);
}

/** Convenience selector for renderers that want missions vs standalones
 *  rendered with a divider between. */
export function splitTree(tree: MonitorTreeNode[]): {
  missions: MissionTreeNode[];
  standalones: StandaloneHarnessTreeNode[];
  scheduledRuns: ScheduledRunTreeNode[];
} {
  const missions: MissionTreeNode[] = [];
  const standalones: StandaloneHarnessTreeNode[] = [];
  const scheduledRuns: ScheduledRunTreeNode[] = [];
  for (const n of tree) {
    if (n.kind === "mission") missions.push(n);
    else if (n.kind === "scheduled-run") scheduledRuns.push(n);
    else standalones.push(n);
  }
  return { missions, standalones, scheduledRuns };
}

/**
 * Split missions into the ADMISSION QUEUE and everything else.
 *
 * A `queued` mission is not "starting soon" — the admission gate (per-case
 * 1, per-user 5) deliberately parked it behind work that holds the slot. Left
 * inline it reads as idle, sitting among amber-pulsing live rows with only a
 * grey dot to tell them apart, so the pane renders the queue as its own
 * collapsed group.
 *
 * `queued` is returned in FIFO admission order (`openedAt` ascending — the
 * order the gate will actually release them), NOT the "latest activity first"
 * order the main list uses: for a queue, position IS the information. `live`
 * keeps the incoming order untouched.
 */
export function partitionQueuedMissions(missions: MissionTreeNode[]): {
  queued: MissionTreeNode[];
  live: MissionTreeNode[];
} {
  const queued: MissionTreeNode[] = [];
  const live: MissionTreeNode[] = [];
  for (const m of missions) {
    if (m.state === "queued") queued.push(m);
    else live.push(m);
  }
  queued.sort((a, b) => a.openedAt - b.openedAt);
  return { queued, live };
}

/**
 * True when a run is platform PLUMBING rather than the lawyer's work.
 *
 * Reads the harness's OWN declaration (`classification`, projected from its
 * manifest) — never the harnessId, and never the summary string. Both of
 * those were on the table and both were rejected in the handshake with
 * `git-implementation` (thread 20260806T164323Z):
 *
 *   - a harnessId list here is a private client-side copy of a vocabulary the
 *     server owns; a second infra harness would silently miss it, with
 *     nothing going red. This SPA has been bitten by exactly that (07a0a6f7b).
 *   - the summary string (`C2 run · 3 files · ⚠ mass-deletion · a1b2c3d`) is a
 *     byte-stable format contract LexPortal's /checkpoints monitor parses.
 *     Adding a second consumer would re-couple what this decouples.
 *
 * ⚠ **ABSENT ⇒ user work ⇒ VISIBLE.** The `!==` test is deliberate: only an
 * explicit `"infrastructure"` hides a row. A manifest predating the field, a
 * broken projection, or an unrecognised future value all fail toward showing
 * the lawyer their task. Hiding real work is the expensive mistake; showing an
 * infra row is the cheap one.
 */
export function isInfrastructureNode(node: MonitorTreeNode): boolean {
  return (
    node.kind === "standalone-harness" && node.classification === "infrastructure"
  );
}

/**
 * Split monitor nodes into the lawyer's work and the platform's plumbing.
 *
 * The count matters as much as the list: the Tarefas tab count is the headline
 * answer to "how busy are my agents", and on a demo runtime it read 289 — six
 * `case-checkpoint` rows a minute, burying real ingest and case-brief runs.
 * That number was wrong, not merely long, which is why the pane derives its
 * count from `userWork` rather than filtering the list under an unchanged
 * total.
 */
export function partitionInfrastructure<T extends MonitorTreeNode>(
  nodes: readonly T[],
): { userWork: T[]; infrastructure: T[] } {
  const userWork: T[] = [];
  const infrastructure: T[] = [];
  for (const n of nodes) {
    if (isInfrastructureNode(n)) infrastructure.push(n);
    else userWork.push(n);
  }
  return { userWork, infrastructure };
}

/** True when a node is BLOCKED ON THE LAWYER. Distinct from
 *  `isNodeActive` because the two demand different signals: running work
 *  needs no one, whereas an awaiting-user run is stalled until a human
 *  answers. On a tab the lawyer is not looking at, showing both with the
 *  same "spinning" glyph would say "in progress, nothing needed from you"
 *  about a run that is in fact waiting on them. */
export function isNodeAwaitingUser(node: MonitorTreeNode): boolean {
  if (node.kind === "scheduled-run") return false;
  if (node.kind === "mission") return node.state === "awaiting_user";
  return node.status === "awaiting_user";
}

/** True when a node represents work still in flight — drives the TAB
 *  ACTIVITY INDICATOR so the lawyer can tell that the tab they are NOT
 *  looking at has something running. Scheduled runs are log rows and are
 *  never "active" by construction. */
export function isNodeActive(node: MonitorTreeNode): boolean {
  if (node.kind === "scheduled-run") return false;
  if (node.kind === "mission") return isActiveMissionState(node.state);
  return (
    node.status === "queued" ||
    node.status === "dispatched" ||
    node.status === "running" ||
    node.status === "awaiting_user"
  );
}
